import detectEthereumProvider from '@metamask/detect-provider'
import getSymbolFromCurrency from 'currency-symbol-map'
import LightTooltip from 'components/LightTooltip'
import { HARMONIZE, HARMONIZE_ADDRESS } from 'abi/Harmonize'
import {
    getTokenHasPrivileges,
    getTokenId

} from './tokenUtils'
import { setEthAccount } from 'redux/actions/accountActions'
import factory from '../redux/store'
import { ethers } from 'ethers'
import { ROYALTY_DIVISOR } from './constants'
import BN from 'bn.js'
const { store } = factory()

const infuraGasEstimateApi = `https://gas.api.infura.io/v3/${process.env.REACT_APP_INFURA_API_KEY}/networks/${process.env.REACT_APP_ACTIVE_CHAIN_ID}/suggestedGasFees`
export const IPFS_CID_LENGTHS = { FIRST: 32, SECOND: 14, TOTAL: 46 }

const CHAINS = [
    { id: 1, name: 'mainnet' },
    { id: 11155111, name: 'sepolia' }
]
const { Web3 } = require('web3')

export const NFT_ADDRESS = HARMONIZE_ADDRESS
export const NFT_SYMBOL = 'HARMONIZE'
let loadTimer
let abortController
let web3Provider
const ORACLE_TIMEOUT = 10000
export const checkAddress = (address) => {
    const web3 = getProvider()
    if (web3) {
        return web3.utils.isAddress(address)
    } else {
        return false
    }
}
function decodeRevertReason(errorData) {
    if (errorData && errorData.startsWith('0x08c379a0')) {
        try {
            const encodedMessage = '0x' + errorData.slice(10) // Remove function selector
            const decoded = new ethers.AbiCoder().decode(['string'], encodedMessage)
            return decoded[0]
        } catch (e) {
            return `Failed to decode revert reason: ${e.message}`
        }
    } else {
        return 'No revert reason found or unknown error format'
    }
}
/**Attempt to decode a HarmonizeError thrown from the contract. This currently supports Metamask
 * which seems to return an object-string that contains the string 'execution reverted'. If
 * we find that:
 * - if it contains a message field, we return that
 * - otherwise we decode as though it is custom error data
 * - otherwise we just return the original exception.
 *
 * Decoder from https://blog.soliditylang.org/2021/04/21/custom-errors/
 * @param {*} exc A Metamask exception object that needs to be converted to a string before parsing
 * @returns {message}
 */
export const decodeHarmonizeError = (exc) => {
    console.log('decodeHarmonizeError', exc)
    const metamaskException = exc.toString()
    if (metamaskException.indexOf('TransactionRevertedWithoutReasonError') !== -1) {
        return ({ message: 'Transaction failed, no further information provided' })
    }
    if (
        metamaskException.indexOf('execution reverted') !== -1
    ) {
        const { data, message } = exc
        const matcher = /\[(.*)\]/
        const errorCode = message.match(matcher)
        return { message: errorCode[0] }
        /* This stuff don't work anymore
        // Create a stub abi
        // same name and parameters as the error in the abi.
        const abi = ['function HarmonizeError(string error)']
        try {
            const harmonizeContract = new ethers.Interface(abi)

            const decoded = harmonizeContract.decodeFunctionData(
                harmonizeContract.functions['HarmonizeError(string)'],
                data
            )

            console.log('decodeHarmonizeError result', decoded)
            return { message: decoded.error }
        } catch (error) {
            console.error('decodeHarmonizeError FAILED', error)
        }
            */
    }
    return { message: metamaskException }
}
/**
 * The receipt has two BigInt fields at the root level:
 *  effectiveGasPrice: 2500000034n,
    gasUsed: 151841n,
 * @param {*} receipt 
    @returns {gasUsed,priceInEth}
 */
export const getActualGasCost = (receipt) => {
    const web3 = getProvider()
    if (web3 && receipt) {
        try {
            const { effectiveGasPrice, gasUsed } = receipt

            const priceInWei = new BN(effectiveGasPrice) * new BN(gasUsed)
            const priceInEth = web3.utils.fromWei(`${priceInWei}`, 'ether')
            console.log(`getActualGasCost cost ${priceInWei} in eth ${priceInEth} `)
            return { gasUsed, priceInEth }
        } catch (error) {
            console.error('getActualGasCost FAILED', error)
        }
    } else {
        console.error(
            'Unable to get Web3 provider or maybe the receipt for getActualGasCost'
        )
    }
    return 0
}

/**
 * Display eth, with local price as a tooltip
 * @param {*} eth               Price in eth
 * @param {*} rate              Eth rate in currency
 * @param {*} currency          Currency symbol
 * @param {*} showEthInCurrency True if display is currency and tooltip is eth
 * @returns
 */
export const displayLocalPrice = (
    eth,
    rate,
    currency,
    showEthInCurrency,
    maximumFractionDigits = 6
) => {
    let ethDisplay = eth
    try {
        const ethFloat = parseFloat(eth)
        ethDisplay = ethFloat.toLocaleString(
            undefined, // leave undefined to use the visitor's browser
            // locale or a string like 'en-US' to override it.
            { maximumFractionDigits }
        )
    } catch { }
    if (showEthInCurrency) {
        return (
            <LightTooltip
                placement='bottom-start'
                arrow
                title={`\u{039E}${ethDisplay}`}
            >
                <span style={{ display: 'flex', alignItems: 'center' }}>
                    <b>{getLocalPrice(eth, rate, currency)}</b>
                </span>
            </LightTooltip>
        )
    }
    return (
        <LightTooltip
            arrow
            placement='bottom-start'
            title={getLocalPrice(eth, rate, currency)}
        >
            <span style={{ display: 'flex', alignItems: 'center' }}>
                <b>{`\u{039E}${ethDisplay}`}</b>
            </span>
        </LightTooltip>
    )
}

/**
 *
 * @param {*} eth
 * @param {*} rate
 * @param {*} currency
 * @returns
 */
export const getLocalPrice = (eth, rate, currency) => {
    let localPrice = rate * eth
    try {
        const localFloat = parseFloat(localPrice)
        localPrice = localFloat.toLocaleString(
            undefined, // leave undefined to use the visitor's browser
            // locale or a string like 'en-US' to override it.
            { minimumFractionDigits: 2, maximumFractionDigits: 2 }
        )
    } catch { }
    return `${getSymbolFromCurrency(currency)}${localPrice}`
}

/**
 *
 * @param {*} localPrice
 * @param {*} ethDisplay
 * @param {*} currency
 * @param {*} showEthInCurrency If true show value in local currency, not eth
 * @returns
 */
const getPriceDisplay = (
    localPrice,
    ethDisplay,
    currency,
    showEthInCurrency
) => {
    if (showEthInCurrency) {
        return `${getSymbolFromCurrency(currency)}${localPrice}`
    } else {
        return `\u{039E}${ethDisplay}`
    }
}
/**
 * Either return the eth/local price as a string, or, if label is provided,
 * as a LightTooltip-wrapped span with the label as a prefix.
 * @param {*} eth
 * @param {*} rate                May be 0 if not yet set, in which case we force eth display
 * @param {*} currency
 * @param {*} showEthInCurrency
 * @param {*} label
 * @returns
 */
export const getEthOrLocalPrice = (
    eth,
    rate,
    currency,
    showEthInCurrency,
    label
) => {
    const doShowEthInCurrency = rate !== 0 ? showEthInCurrency : false
    const ethFloat = parseFloat(eth)
    const ethDisplay = ethFloat.toLocaleString(
        undefined, // leave undefined to use the visitor's browser
        // locale or a string like 'en-US' to override it.
        { maximumFractionDigits: 6 }
    )
    let localPrice = rate * eth
    try {
        const localFloat = parseFloat(localPrice)
        localPrice = localFloat.toLocaleString(
            undefined, // leave undefined to use the visitor's browser
            // locale or a string like 'en-US' to override it.
            { minimumFractionDigits: 2, maximumFractionDigits: 2 }
        )
    } catch { }

    if (label) {
        return (
            <LightTooltip
                placement='top'
                arrow
                title={showEthInCurrency ? `\u{039E}${ethDisplay}` : localPrice}
            >
                <span>
                    {label}:{' '}
                    {getPriceDisplay(
                        localPrice,
                        ethDisplay,
                        currency,
                        doShowEthInCurrency
                    )}
                </span>
            </LightTooltip>
        )
    } else {
        return getPriceDisplay(
            localPrice,
            ethDisplay,
            currency,
            doShowEthInCurrency
        )
    }
}
/**
 * When the detectEthereumProvider returns, it means something has changed.
 * Call window.reload to force a reread of the eth account.
 * @param {*} reset If true set the eth account true so as not to rely on redux store
 */
export const listenForAccountsChange = async (reset) => {
    const provider = await detectEthereumProvider()
    if (provider) {
        provider.on('accountsChanged', (accounts) => {
            console.log('accountsChanged accounts', accounts)
            //if (reset) {
            store.dispatch(setEthAccount(null))
            //}
            window.location.reload()
        })

    }
}
export const listenForChainChange = async () => {
    const provider = await detectEthereumProvider()
    if (provider) {
        provider.on('chainChanged', (chainId) => {
            console.log(`chainChanged ${chainId}`)
            window.location.reload()
        })
    }
}

export const ethEnabled = async () => {
    if (window.ethereum) {
        const enabled = await window.ethereum.request({ method: 'eth_requestAccounts' })// window.ethereum.enable()
        return enabled
    }
    return false
}
export const getProvider = () => {
    if (!web3Provider) {
        if (typeof window.ethereum !== 'undefined') {
            const web3 = new Web3(window.ethereum)
            console.log('getProvider returned', web3)
            if (web3) {
                web3Provider = web3
                return web3
            }
        }
        throw new Error('Unable to get Web3 provider')
    } else {
        return web3Provider
    }

}

export const getChainId = async () => {
    const web3 = getProvider()
    if (web3) {
        const chainId = await web3.eth.getChainId()
        console.log(`chainId ${chainId}`)
        return chainId
    } else {
        return null
    }
}

/**
 *
 * @returns A value from CHAINS, or null if not found
 */
export const getActiveChainName = () => {
    const chainId = parseInt(process.env.REACT_APP_ACTIVE_CHAIN_ID)
    const chain = CHAINS.find((c) => c.id === chainId)
    if (chain) {
        return chain.name
    } else {
        return null
    }
}
export const isCorrectChainId = async () => {
    const chainId = await getChainId()
    return chainId == process.env.REACT_APP_ACTIVE_CHAIN_ID
}
/**
 * Get the list of accounts from the current provider and return
 * the 0th element
 * @returns Address or null if no accounts
 */
export const getCurrentAccount = async () => {
    const web3 = getProvider()
    if (web3) {
        const accounts = await web3.eth.getAccounts()
        if (accounts && accounts.length) {
            return accounts[0]
        }
    }
    console.error('Unable to getProvider to getCurrentAccount')
    return null
}

export const getAccountBalance = async () => {
    let balance
    const web3 = getProvider()
    if (web3) {
        const accounts = await web3.eth.getAccounts()
        if (accounts && accounts.length) {
            const currentAccount = accounts[0]
            balance = await web3.eth.getBalance(currentAccount)
        }
    } else {
        console.error('Unable to getProvider to getAccountBalance')
        return -1
    }
    return balance
}

export const getAccountBalanceEth = async () => {
    let displayBalance
    const web3 = getProvider()
    if (web3) {
        const acctBalance = await getAccountBalance()
        //Crashes if the 'ether' parameter is not provided
        displayBalance = web3.utils.fromWei(acctBalance, 'ether')
    } else {
        console.error('Unable to getProvider to getAccountBalanceEth')
    }
    return displayBalance
}
export const getContractBalance = async () => {
    const web3 = getProvider()
    const accounts = await web3.eth.getAccounts()
    //console.log('web3 accounts', accounts)
    const currentAccount = accounts[0]
    const splunge = getHarmonizeContract()
    const balance = await splunge.methods
        .balanceOf(currentAccount)
        .call({ from: currentAccount })
    console.log(
        `getContractBalance currentAccount ${currentAccount} contractBalance ${balance}`
    )
    return balance
}

/**
 * Use the ownerOf contract method to get the owner of the token.
 * Call is made using current account.
 * @param {*} tokenId
 * @returns
 */
export const getOwner = async (tokenId) => {
    const harmonizeContract = getHarmonizeContract()
    const currentAccount = await getCurrentAccount()
    if (harmonizeContract && currentAccount) {
        const owner = await harmonizeContract.methods
            .ownerOf(tokenId)
            .call({ from: currentAccount })
        return owner
    } else {
        return null
    }
}

/**
*  Chechk if current account owns the token.
 * @param {*} tokenId 
 * @returns 
 */
export const isOwner = async (tokenId) => {
    const currentAccount = await getCurrentAccount()
    const owner = await getOwner(tokenId)
    return owner.toLowerCase() === currentAccount.toString().toLowerCase()
}

/** Get the ownerTokens from the server.  */
export const getOwnerTokens = async () => {
    /* Get tokens for registered Users, not for ETH accounts
    const web3 = getProvider()
    const accounts = await web3.eth.getAccounts()
    const currentAccount = accounts[0]
    const tokens = await getOwnedTokens(currentAccount)
    if (tokens) {
        return [...tokens.assets, ...tokens.albums]
    } else {
        return []
    }
        */
}

/**
 * Get the partner shares for the signed-in User.
 * @param {*} tokenId     Token ID
 * @returns The partner shares uint256 or null if none
 */
export const getPartnerShares = async (tokenId) => {
    /* Get tokens for registered Users, not for ETH accounts
const web3 = getProvider()
if (web3) {
    const accounts = await web3.eth.getAccounts()
    const currentAccount = accounts[0]
    if (currentAccount) {
        const harmonizeContract = getHarmonizeContract()
        const owner = await harmonizeContract.methods
            .ownerOf(getTokenId(tokenId))
            .call({ from: currentAccount })
        const partnerShares = await harmonizeContract.methods
            .getPartnerShares(owner, getTokenId(tokenId), currentAccount)
            .call({ from: currentAccount })
        return partnerShares
    }
}
    */
    return null
}

/**
 * Gets the current issue, if any, for the token
 * @param {*} tokenId   CID of token
 * @returns
 */
export const getTokenIssue = async (tokenId) => {
    const web3 = getProvider()
    if (web3) {
        const accounts = await web3.eth.getAccounts()
        const currentAccount = accounts[0]
        const harmonizeContract = getHarmonizeContract()
        const owner = await harmonizeContract.methods
            .ownerOf(getTokenId(tokenId))
            .call({ from: currentAccount })
        const priceAndIssues = await harmonizeContract.methods
            .priceAndIssues(owner, getTokenId(tokenId))
            .call({ from: currentAccount })
        const issue = priceAndIssues.issue

        return issue
    } else {
        return 0
    }
}
/**
 * Get the sale price for the token. The price in gwei occupies the low-order 32 bits
 * of the priceAndIssue.price field, the remaining bits being reserved for platform-specific
 * use.
 * @param {*} tokenId
 * @param {*} toWei     If true returns the price in wei
 * @returns price in Gwei unless toWei is true
 */
export const getTokenPrice = async (tokenId, toWei) => {
    /* Get this from the Nft table in the Nft Service */
    const web3 = getProvider()
    if (web3) {
        const accounts = await web3.eth.getAccounts()
        const currentAccount = accounts[0]
        const harmonizeContract = getHarmonizeContract()

        const priceInGwei = await harmonizeContract.methods
            .getPrice(tokenId)
            .call({ from: currentAccount })
        const convertedPrice = toWei ? new BN(priceInGwei) * new BN(1000000000)
            : new BN(priceInGwei) / new BN(1000000000)
        return convertedPrice
    } else {
        return 0
    }

}

export const getTokenPrivileged = async (owner, tokenId) => {
    const web3 = getProvider()
    if (web3) {
        const accounts = await web3.eth.getAccounts()
        const currentAccount = accounts[0]
        const harmonizeContract = getHarmonizeContract()
        const priceAndIssues = await harmonizeContract.methods
            .priceAndIssues(owner, getTokenId(tokenId))
            .call({ from: currentAccount })

        return getTokenHasPrivileges(priceAndIssues.price)
    } else {
        return false
    }
}

export const getTokenRoyalties = async (tokenId) => {
    const web3 = getProvider()
    if (web3) {
        const accounts = await web3.eth.getAccounts()
        const currentAccount = accounts[0]
        const harmonizeContract = getHarmonizeContract()
        const royalty = await harmonizeContract.methods
            .creatorRoyalties(getTokenId(tokenId))
            .call({ from: currentAccount })

        return royalty.percentage / ROYALTY_DIVISOR
    } else {
        return 0
    }
}

const cancelTimer = () => {
    if (loadTimer) {
        clearTimeout(loadTimer)
        loadTimer = null
    }
}

/** What etherscan does is produce results where suggestBaseFee is rounded to the
 * nearest integer, then the difference between that and the listed prices is
 * the priority fee.
 * 
 * See https://docs.etherscan.io/api-endpoints/gas-tracker
 */
export const getOracle = async () => {
    try {
        cancelTimer()
        abortController = new AbortController()
        loadTimer = setTimeout(() => {
            abortController.abort()
        }, ORACLE_TIMEOUT)

        const request = await fetch(infuraGasEstimateApi, {
            signal: abortController.signal,
        })
        cancelTimer()
        const oracleResponse = await request.json()
        console.log('getOracle', oracleResponse)
        const { low, medium, high } =
            oracleResponse
        return { low, medium, high }
    } catch (e) {
        console.error('Unable to retrieve oracle', e)
    }

    return {
        low: [60, 60],
        average: [80, 80],
        high: [100, 100],
    }
}
export const getAllETHRates = async () => {
    try {
        const request = await fetch(
            `https://api.coinbase.com/v2/exchange-rates?currency=eth`
        )
        const ethResponse = await request.json()

        return ethResponse.data.rates
    } catch (e) {
        console.error('Unable to retrieve ETH rates', e)
    }
    return 0
}
export const getETHRate = async (currency) => {
    const allRates = await getAllETHRates()
    const ratesMap = new Map(Object.entries(allRates))
    return ratesMap.get(currency)
}

/**
 * Uses the infura gas API

 * @returns high priority suggestedMaxFeePerGas
 */
const getLatestFees = async () => {
    const oracle = await getOracle()
    console.log('oracle', oracle)
    const { low, medium, high } = oracle
    let maxFeePerGas = high.suggestedMaxFeePerGas
    console.log(`maxFeePerGas ${maxFeePerGas}`)
    return maxFeePerGas
}

/**
 * For any payable method, you must provide value even if it is 0.
 * Otherwise you get "value at "/0" is required"
 * @param {*} ethAccount
 * @param {*} value in ether
 * @returns
 */
const getTransactionParams = async (ethAccount, value) => {
    const currentAccount = ethAccount ? ethAccount : await getCurrentAccount()
    const params = {
        from: currentAccount,
    }
    const web3 = getProvider()
    params.value = web3.utils.toWei(value ? value : '.001', 'ether')

    console.log(`getTransactionParams`, params)
    return params
}

/**
 * 
 * @param {*} maxFeePerGas 
 * @param {*} gasEstimate For some reason this is an object
 * @returns Cost in eth (for display purposes)
 */
const getGasCostInEth = (maxFeePerGas, gasEstimate) => {
    console.log(`getGasCostInEth gasEstimate ${gasEstimate} maxFeePerGas ${maxFeePerGas}`)
    const estimateInGwei = maxFeePerGas * new BN(gasEstimate)
    console.log(
        `getGasCostInEth  estimateInGwei ${estimateInGwei}`
    )
    return estimateInGwei / new BN('1000000000')
}

export const getGasEstimateToSetOptions = async (options) => {
    console.log(`getGasEstimateToSetOptions ${options}`)
    const harmonizeContract = getHarmonizeContract()
    if (harmonizeContract) {
        const gasEstimate = await harmonizeContract.methods
            .setOptions(`${options}`)
            .estimateGas(await getTransactionParams())
        const maxFeePerGas = await getLatestFees()
        const gasInEth = getGasCostInEth(maxFeePerGas, gasEstimate)

        return { gasEstimate, gasInEth }
    }
    return null
}

/**
 *
 * @param {*} tokenId
 * @param {*} tokenPrice Price in eth
 * @param {*} transactionFee
 * @returns
 */
export const getGasEstimateToSetPrice = async (
    tokenId,
    tokenPrice,
    transactionFee
) => {
    console.log(
        `getGasEstimateToSetPrice ${tokenId}, ${tokenPrice}, ${transactionFee}`
    )
    const harmonizeContract = getHarmonizeContract()
    const priceInGwei = getGweiFromEth(tokenPrice)
    if (harmonizeContract) {
        const gasEstimate = await harmonizeContract.methods
            .setPrice(tokenId, priceInGwei)
            .estimateGas(await getTransactionParams(undefined, transactionFee))
        const maxFeePerGas = await getLatestFees()
        const gasInEth = getGasCostInEth(maxFeePerGas, gasEstimate)

        console.log(
            `Estimated gas units ${gasEstimate} max gas fee per unit ${maxFeePerGas} total gas in eth ${gasInEth}`
        )
        return { gasEstimate, gasInEth }
    }
    return null
}

/**

 * @param {*} tokenId
 * @param {*} tokenPrice
 * @returns
 */
export const getGasEstimateToPurchase = async (tokenId, tokenPrice) => {
    const harmonizeContract = getHarmonizeContract()
    const web3 = getProvider()
    const priceInWei = web3.utils.toWei(`${tokenPrice}`)
    if (harmonizeContract) {
        const owner = await getOwner(tokenId)
        console.log(`getGasEstimateToPurchase ${tokenPrice} owner ${owner}`)
        const gasEstimate = await harmonizeContract.methods
            .purchaseToken(owner, tokenId)
            .estimateGas(await getTransactionParams(null, `${priceInWei}`))
        const maxFeePerGas = await getLatestFees()
        const gasInEth = getGasCostInEth(maxFeePerGas, gasEstimate)
        console.log(
            `getGasEstimateToPurchase gasEstimate ${gasEstimate} cost in eth ${gasInEth} owner ${owner} tokenId ${getTokenId(
                tokenId
            )}`
        )
        return { gasEstimate, gasInEth }
    }
    return null
}

/**
 *
 * @param {*} tokenId
 * @param {*} transactionFee
 * @param {*} to
 * @returns
 */
export const getGasEstimateToTransfer = async (tokenId, to, transactionFee) => {
    console.log(`getGasEstimateToTransfer ${tokenId} ${to} ${transactionFee}`)
    const harmonizeContract = getHarmonizeContract()
    const currentAccount = await getCurrentAccount()
    if (currentAccount) {
        const web3 = getProvider()
        if (web3.utils.isAddress(to)) {
            const gasEstimate = await harmonizeContract.methods
                .transferToken(to, getTokenId(tokenId))
                .estimateGas(await getTransactionParams(null, transactionFee))
            const maxFeePerGas = await getLatestFees()
            const gasInEth = getGasCostInEth(maxFeePerGas, gasEstimate)

            return { gasEstimate, gasInEth }
        }
    }
    return null
}
/**
 * Multiply estimate by 1.5 because that is what Metamask seems to do
 * @param {*} gasEstimate Currently we are using maxFeePerGas
 * @returns
 */
const metamaskGasCostEstimate = (gasEstimate) => {
    return Math.round(gasEstimate * 1.5)
}

/** A metadataCID string must be split into two bytes32 for submission to the
 * TruePicture contract. The IPFS CID
 * @param {*} metadataCIDs Array of metadataCID strings
 * @returns {[metaCIDs1],[metaCIDs2]}
 */
export const convertMetadataCIDs = (metadataCIDs) => {
    const web3 = getProvider()
    const metaCIDs1 = metadataCIDs.map((e) =>
        web3.utils.asciiToHex(e.substring(0, IPFS_CID_LENGTHS.FIRST))
    )
    const metaCIDs2 = metadataCIDs.map((e) =>
        web3.utils.asciiToHex(
            e.substring(IPFS_CID_LENGTHS.FIRST, IPFS_CID_LENGTHS.TOTAL)
        )
    )

    return { metaCIDs1, metaCIDs2 }
}

/**
 *Check to see if the current account ETH balance is more than 150% of the
 * estimated gas cost plus any msg value.
 * @param {*} gasEstimate {gasInEth}
 * @param {*} ethBalance
 * @param {*} value        Value sent, if any (e.g. for purchase)
 * @returns
 */
export const isSafeToMint = (gasEstimate, ethBalance, value = 0) => {
    //console.log(`isSafeToMint value ${value} bal ${ethBalance}`, gasEstimate)
    if (gasEstimate) {
        const gasCostEth = parseFloat(gasEstimate.gasInEth)
        const margin = ethBalance - value - 1.5 * gasCostEth
        //console.log(`...margin ${margin}`)
        return margin > 0
    }
    console.error('isSafeToMint called without a gasEstimate')
    return false
}

/**

 * @param setter Callback to set Gas price in Gwei
 * @returns {price,prGwei}
 */
export const getGasPrice = async (setter) => {
    const web3 = getProvider()
    const price = await web3.eth.getGasPrice()
    console.log(`getGasPrice ${price}`)
    const prWei = new BN(price)
    const prGwei = web3.utils.fromWei(`${prWei}`, 'gwei')
    if (setter) {
        setter(prGwei)
    }
    console.log(`Current gas price wei ${prWei} gwei ${prGwei}`)
    return { price, prGwei }
}

/** Currently UNUSED */
export const getTokenUri = async (tokenId, account) => {
    console.log(`getTokenUri ${tokenId}, ${account}`)
    const web3 = getProvider()
    let currentAccount = account
    if (!currentAccount) {
        const accounts = await web3.eth.getAccounts()
        currentAccount = accounts[0]
    }
    const harmonizeContract = getHarmonizeContract()
    const tokenUri = await harmonizeContract.methods
        .tokenURI(getTokenId(tokenId))
        .call({ from: currentAccount })
    //console.log(`currentAccount ${currentAccount} tokenUri ${tokenUri}`)
    return tokenUri
}

/**
 * The Tru token splits the metadata CID into bytes32 and a bytes16 fields.
 * This method joins and returns them. This should only be required when directly access
 * the contract's truTokens array.
 *
 * Note that hexToAscii does not remove trailing 0s from the original data, and so we
 * use hexToUtf8.
 * @param {*} token
 * @returns
 */
export const joinMetadataCID = (token) => {
    const { metadataCID1, metadataCID2 } = token
    const web3 = getProvider()
    return `${web3.utils.hexToUtf8(metadataCID1)}${web3.utils.hexToUtf8(
        metadataCID2
    )}`
        .substring(0, 46)
        .trim()
}

export const isApprovedForAll = async () => {
    console.log('isApprovedForAll')
    const web3 = getProvider()
    const accounts = await web3.eth.getAccounts()
    const currentAccount = accounts[0]
    const harmonizeContract = getHarmonizeContract()

    const approved = await harmonizeContract.methods
        .isApprovedForAll(currentAccount, HARMONIZE_ADDRESS)
        .call({ from: currentAccount })
    console.log(`contract approval state ${approved}`)

    return approved
}

export const getHarmonizeContract = (contractAddress = HARMONIZE_ADDRESS) => {
    try {
        const web3 = getProvider()
        console.log('getHarmonizeContract')
        const harmonizeContract = new web3.eth.Contract(HARMONIZE.abi, contractAddress)
        console.log('...got', harmonizeContract)
        return harmonizeContract
    } catch (error) {
        throw new Error('Failed to get Harmonize contract', { cause: error })
    }
}

export const getTruSymbol = async () => {
    let symbol
    const web3 = getProvider()
    if (web3) {
        const accounts = await web3.eth.getAccounts()
        const currentAccount = accounts[0]

        const harmonizeContract = getHarmonizeContract()
        symbol = await harmonizeContract.methods.symbol().call({ from: currentAccount })
    }
    return symbol
}

const processTransfer = (transfer) => {
    console.log('processTransfer received', transfer)
    return { status: 'Sold' }
}

/**
 * By observation, if we take the high priority fee from Etherscan and submit that
 * to metamask as both fees, we are in alignment. If we leave out either fee,
 * we get a "Network doesn't suport eip-1559" error.
 */
export const deployContract = async (completed, failed) => {
    const harmonizeContract = getHarmonizeContract()
    const adminAccount =
        process.env.REACT_APP_ETH_TEST === 'true'
            ? process.env.REACT_APP_ROPSTEN_CONTRACT_ADMIN
            : process.env.REACT_APP_CONTRACT_ADMIN
    const { bytecode } = HARMONIZE
    harmonizeContract
        .deploy({
            data: bytecode,
        })
        .send(await getTransactionParams(adminAccount))
        .on('error', function (error) {
            console.error('deploy FAILED', error)
            failed(error)
        })
        .once('transactionHash', function (txHash) {
            console.log(`deploy received transactionHash ${txHash}`)
        })
        .on('receipt', function (receipt) {
            console.log(`Receipt, new address ${receipt.contractAddress}`)

            completed(receipt)
        })
        .on('confirmation', function (confirmationNumber, receipt) {
            console.log('Confirmed')
        })
        .then(function (newContractInstance) {
            console.log(
                `Completed, new address ${newContractInstance.options.address}`
            ) // instance with the new contract address
        })
}

/**
 *
 * @param {*} eth Eth value. Max specified in env file
 * @returns eth multiplied by 1 billion as decimal string from BN, or '0' if eth is undefined
 */
export const getGweiFromEth = (eth) => {
    if (eth) {
        const ethGwei =
            Math.min(eth, parseInt(process.env.REACT_APP_MAX_TOKEN_PRICE_ETH)) *
            10 ** 9
        return ethGwei
    } else {
        return '0'
    }
}

/**
 * 
 * @param {*} tokenId       The uint256 string for the NFT
 * @param {*} priceInGwei 
 * @param {*} mintFee 
 * @returns 
 */
export const getGasEstimateToMint = async (
    tokenId,
    priceInGwei,
    mintFee
) => {
    console.log(`getGasEstimateToMint tokenId ${tokenId} priceInGwei ${priceInGwei} mintFee ${mintFee} `)
    const harmonizeContract = getHarmonizeContract()
    const currentAccount = await getCurrentAccount()
    if (harmonizeContract && currentAccount) {
        const partners = []
        const params = await getTransactionParams(currentAccount, mintFee)
        const gasEstimate = await harmonizeContract.methods
            .mint(tokenId, `${priceInGwei}`, '0',
                '1',//hash
                '2',//fingerprint
                ethers.encodeBytes32String('test'),
                partners,
                {
                    value: '0',
                })
            .estimateGas(params)
        const maxFeePerGas = await getLatestFees()

        const gasInEth = getGasCostInEth(maxFeePerGas, gasEstimate)

        return { gasEstimate, gasInEth }
    } else {
        return { gasEstimate: 0, gasInEth: 0 }
    }
}
/**
 * Mint a single Vortex token. The tokenId is provided as it has been created from the Room name.
 * 
 * Metamask has no way to set fees using web3. Setting gasPrice is no help. What seems to
 * work is setting "gas" (aka gas limit) at least 2.5 times actual gas estimate. This then
 * results in Maxbase about 1.2 * its "aggressive" setting and a 2.5 priority fee. So
 * that is what we need to show in the estimated cost.

 *
 * When using Metamask, the first time a mint occurs, it will show a "new address" box. This is
 * because it is considering the contract address as the target of the send, and it has not seen this
 * address. If you save this address in Metamask, you will not see this prompt again.

 * @param {*} tokenId              uint256 string ID for new token
 * @param {*} priceInGwei       Price in gwei with any extra bits already set for the Asset to mint.
 * @param {*} royalty           uint256
 * @param {*} hash              uint256 CANNOT BE 0 or mint will fail
 * @param {*} finger            uint256
 * @param {*} id                bytes32
 * @param {*} partners          array of Partner struct
 * @param {*} mintFee           In wei
 * @param {*} txStart           Callback when transactionHash is received
 * @param {*} completed         Callback when transaction succeeds
 * @param {*} failed            Callback if transaction fails
 */
export const mintHarmonize = async (
    tokenId,
    priceInGwei,
    royalty,
    hash,
    finger,
    idString,
    partners,
    mintFee,
    txStart,
    completed,
    failed
) => {
    const harmonizeContract = getHarmonizeContract()

    const currentAccount = await getCurrentAccount()
    const id = ethers.encodeBytes32String(idString)
    //If there are errors calling the contract, the getGasEstimateToMint will throw them
    const { gasEstimate } = await getGasEstimateToMint(tokenId, priceInGwei, mintFee)
    const gas = new BN(gasEstimate) 
    console.log(
        `mintHarmonize tokenId ${tokenId} priceInGwei ${priceInGwei} fee ${mintFee} gasEstimate ${gasEstimate}
        gas limit ${gas} `
    )
    const transactionParams = { from: currentAccount, gas: `${gas}` }
    harmonizeContract.methods
        .mint(tokenId, `${priceInGwei}`, `${royalty}`,
            `${hash}`,
            `${finger}`,
            `${id}`,
            partners
        )
        .send(transactionParams)
        .once('transactionHash', function (txHash) {
            console.log(`mintHarmonize received transactionHash ${txHash}`)
            txStart(tokenId, txHash)
        })
        .once('receipt', function (receipt) {
            console.log(`mintHarmonize received receipt`, receipt)
        })
        .on('error', function (error) {
            failed(error)
        })
        .then(function (receipt) {
            completed(receipt)
        })
        .catch((error) => {
            //Metamask transaction cancelled ends up here
            failed(error)
        })
}

/**
 *
 * @param {*} tokenOwner
 * @param {*} tokenId
 * @param {*} partner
 * @param {*} numShares
 * @param {*} sharePrice
 * @param {*} completed
 * @param {*} failed
 */
export const buyPartnerShares = async (
    tokenOwner,
    tokenId,
    partner,
    numShares,
    sharePrice,
    completed,
    failed
) => {
    const harmonizeContract = getHarmonizeContract()
    if (harmonizeContract) {
        //First call to throw any errors
        const web3 = getProvider()
        const totalPriceEth = numShares * sharePrice
        const totalCostWei = web3.utils.toWei(`${totalPriceEth}`)
        await harmonizeContract.methods
            .buyPartnerShares(
                tokenOwner,
                getTokenId(tokenId),
                partner,
                `${numShares}`
            )
            .call(await getTransactionParams(null, `${totalCostWei}`))
        console.log('buyPartnerShares OK')
        harmonizeContract.methods
            .buyPartnerShares(
                tokenOwner,
                getTokenId(tokenId),
                partner,
                `${numShares}`
            )
            .send(await getTransactionParams(null, `${totalCostWei}`))
            .once('transactionHash', function (txHash) {
                console.log(`buyPartnerShares received transactionHash ${txHash}`)
            })
            .once('receipt', function (receipt) {
                console.log(`buyPartnerShares received receipt`, receipt)
            })
            .on('error', function (error) {
                failed(error)
            })
            .then(function (receipt) {
                completed(receipt)
            })
    }
}

/**
 * 
 * @param {*} tokenOwner 
 * @param {*} tokenId 
 * @param {*} numShares 
 * @param {*} sharePrice 
 * @param {*} completed 
 * @param {*} failed 
 */
export const buyTokenShares = async (
    tokenOwner,
    tokenId,
    numShares,
    sharePrice,
    completed,
    failed
) => {
    const harmonizeContract = getHarmonizeContract()
    if (harmonizeContract) {
        //First call to throw any errors
        const web3 = getProvider()
        const totalPriceEth = numShares * sharePrice
        const totalCostWei = web3.utils.toWei(`${totalPriceEth}`)
        await harmonizeContract.methods
            .buyShares(tokenOwner, getTokenId(tokenId), `${numShares}`)
            .call(await getTransactionParams(null, `${totalCostWei}`))
        console.log('buyTokenShares OK')
        harmonizeContract.methods
            .buyShares(tokenOwner, getTokenId(tokenId), `${numShares}`)
            .send(await getTransactionParams(null, `${totalCostWei}`))
            .once('transactionHash', function (txHash) {
                console.log(`buyTokenShares received transactionHash ${txHash}`)
            })
            .once('receipt', function (receipt) {
                console.log(`buyTokenShares received receipt`, receipt)
            })
            .on('error', function (error) {
                failed(error)
            })
            .then(function (receipt) {
                completed(receipt)
            })
    }
}

/**
 * 
 * @param {*} tokenOwner 
 * @param {*} tokenId 
 * @param {*} numShares 
 * @param {*} transactionFee 
 * @returns 
 */
export const getGasEstimateToBuyTokenShares = async (
    tokenOwner,
    tokenId,
    numShares,
    transactionFee) => {
    console.log(`getGasEstimateToBuyTokenShares ${tokenId} ${tokenOwner} ${transactionFee}`)
    const harmonizeContract = getHarmonizeContract()
    const currentAccount = await getCurrentAccount()
    if (currentAccount) {
        const web3 = getProvider()
        if (web3.utils.isAddress(tokenOwner)) {
            const gasEstimate = await harmonizeContract.methods
                .buyShares(tokenOwner, getTokenId(tokenId), `${numShares}`)
                .estimateGas(await getTransactionParams(null, transactionFee))
            const maxFeePerGas = await getLatestFees()
            const gasInEth = getGasCostInEth(maxFeePerGas, gasEstimate)

            return { gasEstimate, gasInEth }
        }
    }
    return null
}

/**
 * @param {*} tokenId
 * @param {*} numShares
 * @param {*} pricePerShare     Price in eth
 * @param {*} transactionFee
 * @param {*} completed
 * @param {*} failed
 */
export const createPartnerIssue = async (
    tokenId,
    numShares,
    pricePerShare,
    transactionFee,
    completed,
    failed
) => {
    const harmonizeContract = getHarmonizeContract()
    const currentAccount = await getCurrentAccount()
    if (harmonizeContract && currentAccount) {
        const priceInGwei = `${getGweiFromEth(pricePerShare)}`

        const owner = await harmonizeContract.methods
            .ownerOf(getTokenId(tokenId))
            .call({ from: currentAccount })
        //First call to throw any errors

        await harmonizeContract.methods
            .partnerOffering(owner, getTokenId(tokenId), `${numShares}`, priceInGwei)
            .call(await getTransactionParams(null, transactionFee))
        console.log('createPartnerIssue OK')
        harmonizeContract.methods
            .partnerOffering(owner, getTokenId(tokenId), `${numShares}`, priceInGwei)
            .send(await getTransactionParams(null, transactionFee))
            .once('transactionHash', function (txHash) {
                console.log(`createPartnerIssue received transactionHash ${txHash}`)
            })
            .once('receipt', function (receipt) {
                console.log(`createPartnerIssue received receipt`, receipt)
            })
            .on('error', function (error) {
                failed(error)
            })
            .then(function (receipt) {
                completed(receipt)
            })
    }
}
/**
 *
 * @param {*} tokenId
 * @param {*} issue
 * @param {*} transactionFee
 * @param {*} completed
 * @param {*} failed
 */
export const createTokenIssue = async (
    tokenId,
    issue,
    transactionFee,
    completed,
    failed
) => {
    const harmonizeContract = getHarmonizeContract()
    if (harmonizeContract) {
        //First call to throw any errors
        console.log(`createTokenIssue transactionFee ${transactionFee}`)
        await harmonizeContract.methods
            .offering(getTokenId(tokenId), `${issue}`)
            .call(await getTransactionParams(null, transactionFee))
        console.log('createTokenIssue OK')
        harmonizeContract.methods
            .offering(getTokenId(tokenId), `${issue}`)
            .send(await getTransactionParams(null, transactionFee))
            .once('transactionHash', function (txHash) {
                console.log(`createTokenIssue received transactionHash ${txHash}`)
            })
            .once('receipt', function (receipt) {
                console.log(`createTokenIssue received receipt`, receipt)
            })
            .on('error', function (error) {
                failed(error)
            })
            .then(function (receipt) {
                completed(receipt)
            })
    }
}
/**
 *
 * @param {*} options
 * @param {*} completed
 * @param {*} failed
 */
export const setContractOptions = async (options, completed, failed) => {
    const harmonizeContract = getHarmonizeContract()
    if (harmonizeContract) {
        harmonizeContract.methods
            .setOptions(`${options}`)
            .send(await getTransactionParams())
            .once('transactionHash', function (txHash) {
                console.log(`setContractOptions received transactionHash ${txHash}`)
            })
            .once('receipt', function (receipt) {
                console.log(`setContractOptions received receipt`, receipt)
            })
            .on('error', function (error) {
                failed(error)
            })
            .then(function (receipt) {
                completed(receipt)
            })
    }
}
/**
 *
 * @param {*} tokenId
 * @param {*} tokenPrice  Price in eth
 * @param {*} transactionFee
 * @param {*} completed
 * @param {*} failed
 * @param {*} started
 * @returns
 */
export const setPrice = async (
    tokenId,
    tokenPrice,
    transactionFee,
    completed,
    failed,
    started
) => {
    console.log(`setPrice ${tokenId} ${tokenPrice}`)
    const harmonizeContract = getHarmonizeContract()
    const priceInGwei = `${getGweiFromEth(tokenPrice)}`
    if (harmonizeContract) {
        const { gasEstimate } = await getGasEstimateToSetPrice(tokenId, priceInGwei)
        const gas = new BN(gasEstimate) //* new BN(7)
        console.log(
            `setPrice tokenId ${tokenId} gasEstimate ${gasEstimate}
        gas limit ${gas} `
        )
        const currentAccount = await getCurrentAccount()
        const transactionParams = { from: currentAccount, gas: `${gas}` }
        harmonizeContract.methods
            .setPrice(getTokenId(tokenId), priceInGwei)
            .send(transactionParams)
            .once('transactionHash', function (txHash) {
                console.log(`setPrice received transactionHash ${txHash}`)
                started(txHash)
            })
            .once('receipt', function (receipt) {
                console.log(`setPrice received receipt ${receipt}`)
            })
            .on('error', function (error) {
                failed(error)
            })
            .then(function (receipt) {
                completed(receipt)
            })
            .catch((error) => {
                //Metamask transaction cancelled ends up here
                failed(error)
            })
    }
}

/**
 * Note that the send method does not throw exceptions, only the call. See
 * https://ethereum.stackexchange.com/questions/86838/web3-error-handling-in-react
 * @param {*} owner
 * @param {*} tokenId
 * @param {*} currentPrice
 * @param {*} completed
 * @param {*} failed
 * @returns
 */
export const purchase = async (
    owner,
    tokenId,
    currentPrice,
    started,
    completed,
    failed
) => {
    console.log(`purchase ${tokenId}, ${currentPrice}`)

    const harmonizeContract = getHarmonizeContract()

    if (harmonizeContract) {
        const web3 = getProvider()
        const priceInWei = web3.utils.toWei(`${currentPrice}`)
        try {
            const transactionParams = {
                ...(await getTransactionParams()),
                value: `${priceInWei}`,
            }
            //First call to throw any errors
            await harmonizeContract.methods
                .purchaseToken(owner, getTokenId(tokenId))
                .call(transactionParams)
            const transfer = await harmonizeContract.methods
                .purchaseToken(owner, getTokenId(tokenId))
                .send(transactionParams)
                .once('transactionHash', function (txHash) {
                    console.log(`purchase received transactionHash ${txHash}`)
                    started(txHash)
                })
                .once('receipt', function (receipt) {
                    console.log(`purchase received receipt ${receipt}`)
                })
                .on('error', function (error) {
                    failed(error)
                })
                .then(function (receipt) {
                    completed(receipt)
                })

            const result = processTransfer(transfer)

            return result
        } catch (error) {
            throw error
        }
    }
    return null
}

/**
 *
 * @param {*} tokenId
 * @param {*} to
 * @param {*} transactionFee
 * @param {*} completed
 * @param {*} failed
 */
export const transfer = async (
    tokenId,
    to,
    transactionFee,
    completed,
    failed
) => {
    const harmonizeContract = getHarmonizeContract()
    const currentAccount = await getCurrentAccount()
    if (currentAccount) {
        //First call to throw any errors
        await harmonizeContract.methods
            .transferToken(to, getTokenId(tokenId))
            .call(await getTransactionParams(currentAccount, transactionFee))
        harmonizeContract.methods
            .transferToken(to, getTokenId(tokenId))
            .send(await getTransactionParams(currentAccount, transactionFee))
            .once('transactionHash', function (txHash) {
                console.log(`transfer received transactionHash ${txHash}`)
            })
            .once('receipt', function (receipt) {
                console.log(`transfer received receipt ${receipt}`)
            })
            .on('error', function (error) {
                failed(error)
            })
            .then(function (receipt) {
                completed(receipt)
            })
    }
}